<template>
  <div class="team w-10/12 mx-auto">
    <div>
      <div class="flex mb-4">
        <div class="filter-push">
          <input type="radio" v-model="teamFilter.location" value="" id="locationAll" @click="clearFilter">
          <label for="locationAll">{{ labels.all }}</label>
        </div>
        <div class="filter-push"
          v-for="location in locations"
          :key="location.value">
          <input type="radio" v-model="teamFilter.location" :value="location.value" :id="`location-${location.value}`">
          <label :for="`location-${location.value}`">{{ location.label }}</label>
        </div>
      </div>
      <div class="flex flex-wrap items-start align-top">
        <div class="filter-push filter-push--sub mr-2 items-center"
           v-for="specialization in options.specializations"
           :key="specialization">
          <input type="checkbox" :checked="teamFilter.specializations.includes(specialization)" @change="toggleSpecialization(specialization)" :id="specialization">
          <label :for="specialization">{{ specialization }}</label>
        </div>
      </div>
      <transition-group name="member" tag="div" class="team__list flex flex-wrap md:-mx-3 lg:-mx-6 mt-12">
        <div class="w-4/10 md:w-1/4 mb-6 md:mb-12 md:px-3 lg:px-6"
          v-for="member in paginatedMembers"
          :key="member.title">
          <TeamMember :member="member" />
        </div>
      </transition-group>
      <div class="w-full text-center mt-12" v-show="filteredMembers.length === 0 && !isLoading">
        <p class="text-gray">{{ labels.noResults || 'keine Ergebnisse gefunden' }}</p>
      </div>
    </div>
    <div class="text-center mt-8 sm:mt-16" v-show="filteredMembers.length > 0 && !isLoading">
      <button @click="showMore()" class="button" v-show="morePagesAvailable">
        {{ labels.loadmore }}
      </button>
    </div>
    <div class="w-full text-center">
      <Spinner v-if="isLoading" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import TeamMember from './TeamMember'
import Spinner from './Spinner'

const MEMBERS_PER_PAGE = 12

export default {
  props: {
    labels: {
      type: Object,
      default() {
        return {}
      }
    },
    apiBase: {
      type: String,
      default() {
        return ''
      }
    },
    locations: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      isLoading: true,
      page: 1,
      team: [],
      teamFilter: {
        specializations: [],
        location: ''
      },
      options: {
        specializations: [],
      },
    }
  },
  components: {
    Spinner,
    TeamMember
  },
  computed : {
    filteredMembers() {
      let selection = this.team

      if (this.teamFilter.specializations.length) {
        selection = selection.filter(item => {
          return item.specializations.some(c => {
            return this.teamFilter.specializations.includes(c)
          })
        })
      }

      if (this.teamFilter.location !== '') {
        selection = selection.filter(item => {
          const itemLocations = Array.isArray(item.location)
            ? item.location
            : (item.location ? [item.location] : [])
          return itemLocations.includes(this.teamFilter.location)
        })
      }

      selection.sort(function (a, b) {
        const aLast = a.title.toString().split(',')[0].split(' ').splice(-1)
        const bLast = b.title.toString().split(',')[0].split(' ').splice(-1)
        return aLast < bLast ? -1 : 1
      })

      return selection
    },
    paginatedMembers() {
      return this.filteredMembers.slice(0, this.page * MEMBERS_PER_PAGE)
    },
    morePagesAvailable() {
      return this.page < Math.ceil(this.filteredMembers.length / MEMBERS_PER_PAGE)
    },
    filterActive() {
      return this.teamFilter.specializations.length > 0
        || this.teamFilter.location !== ''
    },
  },
  created() {
    const locationMatch = /[?&]location=([^&#]+)/.exec(window.location.search)
    if (locationMatch) {
      const preselect = decodeURIComponent(locationMatch[1])
      if (this.locations.some(location => location.value === preselect)) {
        this.teamFilter.location = preselect
      }
    }

    const API_URL = `${this.apiBase}/api/team.json`
    axios.get(API_URL)
    .then(result => {
      if (result.data && result.data.team) {
        // Normalize location data: extract values from objects if needed
        this.team = result.data.team.map(member => ({
          ...member,
          location: this.normalizeLocation(member.location)
        }))
        this.populate()
        this.isLoading = false
      } else {
        throw new Error('Invalid API response!')
      }
    })
    .catch(error => {
      // eslint-disable-next-line
      console.log('error:', error)
    })
  },
  methods: {
    normalizeLocation(location) {
      if (!location) return []

      // Transformer always returns an array, but handle edge cases
      const locations = Array.isArray(location) ? location : [location]

      // Extract values from objects (fallback if API returns OptionData objects)
      return locations.map(loc => {
        return (typeof loc === 'object' && loc !== null && loc.value !== undefined) ? loc.value : loc
      })
    },

    showMore() {
      this.page++
    },

    populate() {
      let specializations = []

      this.team.forEach(v => specializations.push(...v.specializations))

      this.options.specializations = specializations.filter(function(item, index) {
        return specializations.indexOf(item) >= index
      }).sort()
    },

    toggleSpecialization(specialization) {
      const index = this.teamFilter.specializations.indexOf(specialization)
      if (index > -1) {
        // Remove if already selected (toggle off)
        this.teamFilter.specializations.splice(index, 1)
      } else {
        // Add if not selected (toggle on)
        this.teamFilter.specializations.push(specialization)
      }
    },

    clearFilter() {
      this.teamFilter.specializations = []
      this.teamFilter.location = ''
    },
  }
}
</script>

<style>

.filter {
  @apply text-brand-blue;
}

.filter-push {
  @apply block flex-none mb-1;

  & input {
    display: none;
  }

  & label {
    @apply font-sofia-bold text-sm inline-block text-black text-center w-full h-full px-4 py-1 cursor-pointer;
    background-color: white;
    text-transform: uppercase;
    transition: all 0.1s;
  }

  & input:checked {
    & + label {
      @apply text-brand-blue;
      background-color: rgba(204, 124, 87, 0.4);
      border-radius: 150px;
      text-transform: uppercase;
    }
  }
}

.filter-push--sub {
  & label {
    @apply text-gray;
    text-transform: none;
  }
  & input:checked {
    & + label {
      @apply pb-2;
      color: #777777;
      text-transform: none;
    }
  }
}

@screen mo {
  .team__list > div {
    @apply mx-2;

    &:first-child,
    &:nth-child(odd) {
      @apply ml-0;
    }
  }
}
</style>
